import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

const baseStepLabels = [
  'Product Details',
  'Patient Information',
  'Kit Delivery',
  'Order Review',
];

const mockPatients = [
  {
    id: 'PT-1001',
    firstName: 'Eleanor',
    lastName: 'Bishop',
    dob: '1951-04-19',
    mrn: 'HB-20491',
    address: '214 Cypress Lane, Charleston, SC 29403',
    phone: '(843) 555-0182',
    email: 'eleanor.bishop@example.com',
  },
  {
    id: 'PT-1002',
    firstName: 'Paul',
    lastName: 'Kim',
    dob: '1964-11-03',
    mrn: 'HB-19310',
    address: '92 King Street, Savannah, GA 31401',
    phone: '(912) 555-0144',
    email: 'paul.kim@example.com',
  },
  {
    id: 'PT-1003',
    firstName: 'Miriam',
    lastName: 'Cole',
    dob: '1958-02-27',
    mrn: 'HB-18874',
    address: '18 Westover Drive, Tampa, FL 33602',
    phone: '(813) 555-0176',
    email: 'miriam.cole@example.com',
  },
  {
    id: 'PT-1004',
    firstName: 'Noah',
    lastName: 'Diaz',
    dob: '1970-08-14',
    mrn: 'HB-17762',
    address: '447 River Run Road, Jacksonville, FL 32207',
    phone: '(904) 555-0119',
    email: 'noah.diaz@example.com',
  },
];

export default function MockNewOrderDialog({ open, onClose, selectedTest, onOrderCreated }) {
  const [activeStep, setActiveStep] = React.useState(0);
  const [patientMode, setPatientMode] = React.useState('existing');
  const [patientSearchText, setPatientSearchText] = React.useState('');
  const [selectedPatientId, setSelectedPatientId] = React.useState('');
  const [kitDeliveryOption, setKitDeliveryOption] = React.useState('');
  const [inventoryPaymentOption, setInventoryPaymentOption] = React.useState('');
  const [deliverPaymentOption, setDeliverPaymentOption] = React.useState('');
  const [deliverStreet1, setDeliverStreet1] = React.useState('');
  const [deliverStreet2, setDeliverStreet2] = React.useState('');
  const [deliverCity, setDeliverCity] = React.useState('');
  const [deliverState, setDeliverState] = React.useState('');
  const [deliverZipCode, setDeliverZipCode] = React.useState('');
  const [newFirstName, setNewFirstName] = React.useState('');
  const [newLastName, setNewLastName] = React.useState('');
  const [newDob, setNewDob] = React.useState('');
  const [newMrn, setNewMrn] = React.useState('');
  const [newStreet1, setNewStreet1] = React.useState('');
  const [newStreet2, setNewStreet2] = React.useState('');
  const [newCity, setNewCity] = React.useState('');
  const [newState, setNewState] = React.useState('');
  const [newZipCode, setNewZipCode] = React.useState('');
  const [newTextNumber, setNewTextNumber] = React.useState('');
  const [newEmail, setNewEmail] = React.useState('');
  const [paymentChoice, setPaymentChoice] = React.useState('');
  const [paymentMethod, setPaymentMethod] = React.useState('email');
  const [paymentEmail, setPaymentEmail] = React.useState('');
  const [paymentTextNumber, setPaymentTextNumber] = React.useState('');
  const [paymentQrFullscreen, setPaymentQrFullscreen] = React.useState(false);
  const [paymentCodeScanned, setPaymentCodeScanned] = React.useState(false);
  const qrFullscreenRef = React.useRef(null);

  React.useEffect(() => {
    if (open) {
      setActiveStep(0);
      setPatientMode('existing');
      setPatientSearchText('');
      setSelectedPatientId('');
      setKitDeliveryOption('');
      setInventoryPaymentOption('');
      setDeliverPaymentOption('');
      setDeliverStreet1('');
      setDeliverStreet2('');
      setDeliverCity('');
      setDeliverState('');
      setDeliverZipCode('');
      setNewFirstName('');
      setNewLastName('');
      setNewDob('');
      setNewMrn('');
      setNewStreet1('');
      setNewStreet2('');
      setNewCity('');
      setNewState('');
      setNewZipCode('');
      setNewTextNumber('');
      setNewEmail('');
      setPaymentChoice('');
      setPaymentMethod('email');
      setPaymentEmail('');
      setPaymentTextNumber('');
      setPaymentQrFullscreen(false);
      setPaymentCodeScanned(false);
    }
  }, [open]);

  const includePaymentStep =
    (kitDeliveryOption === 'inventory' && inventoryPaymentOption === 'third-party') ||
    (kitDeliveryOption === 'deliver' && deliverPaymentOption === 'third-party');
  const stepLabels = includePaymentStep ? [...baseStepLabels, 'Payment'] : baseStepLabels;

  React.useEffect(() => {
    setActiveStep((current) => Math.min(current, stepLabels.length - 1));
  }, [stepLabels.length]);

  if (!selectedTest) {
    return null;
  }

  const filteredPatients = mockPatients.filter((patient) => {
    const query = patientSearchText.trim().toLowerCase();
    if (!query) {
      return true;
    }
    return `${patient.firstName} ${patient.lastName} ${patient.dob} ${patient.mrn}`
      .toLowerCase()
      .includes(query);
  });
  const selectedPatient = mockPatients.find((patient) => patient.id === selectedPatientId) || null;
  const patientEmailOptions = [selectedPatient?.email, newEmail].filter(Boolean);
  const patientTextOptions = [selectedPatient?.phone, newTextNumber].filter(Boolean);
  const patientTitleLabel = selectedPatient
    ? `${selectedPatient.firstName} ${selectedPatient.lastName} (${selectedPatient.dob})`
    : newFirstName && newLastName && newDob
      ? `${newFirstName} ${newLastName} (${newDob})`
      : null;
  const knownAddresses = [
    selectedPatient
      ? {
          label: 'Patient Home Address',
          street1: selectedPatient.address.split(',')[0] || '',
          street2: '',
          city: selectedPatient.address.split(',')[1]?.trim()?.split(' ')[0] || '',
          state: selectedPatient.address.split(',')[1]?.trim()?.split(' ')[1] || '',
          zipCode: selectedPatient.address.split(',')[1]?.trim()?.split(' ')[2] || '',
        }
      : null,
    newStreet1 || newCity || newState || newZipCode
      ? {
          label: 'New Patient Address',
          street1: newStreet1,
          street2: newStreet2,
          city: newCity,
          state: newState,
          zipCode: newZipCode,
        }
      : null,
    {
      label: 'Practice Address',
      street1: '145 Harbor Clinic Drive',
      street2: 'Suite 220',
      city: 'Charleston',
      state: 'SC',
      zipCode: '29407',
    },
  ].filter(Boolean);
  const handleDialogClose = (_, reason) => {
    if (reason === 'backdropClick') {
      return;
    }
    onClose();
  };
  const applyKnownAddress = (address) => {
    setDeliverStreet1(address.street1 || '');
    setDeliverStreet2(address.street2 || '');
    setDeliverCity(address.city || '');
    setDeliverState(address.state || '');
    setDeliverZipCode(address.zipCode || '');
  };
  const clearDeliveryAddress = () => {
    setDeliverStreet1('');
    setDeliverStreet2('');
    setDeliverCity('');
    setDeliverState('');
    setDeliverZipCode('');
  };
  const formatAddressLines = (address) => {
    const lineOne = [address.street1, address.street2].filter(Boolean).join(', ');
    const lineTwo = [address.city, address.state, address.zipCode].filter(Boolean).join(', ');
    return [lineOne, lineTwo].filter(Boolean);
  };
  const selectedDeliveryAddressLines = formatAddressLines({
    street1: deliverStreet1,
    street2: deliverStreet2,
    city: deliverCity,
    state: deliverState,
    zipCode: deliverZipCode,
  });
  const paymentSummary =
    kitDeliveryOption === 'inventory'
      ? inventoryPaymentOption === 'no-payment'
        ? {
            title: 'No Payment at this Time',
            detail: 'The practice is handling payment out of band.',
          }
        : inventoryPaymentOption === 'third-party'
          ? {
              title: 'Third Party Payment',
              detail: 'Payment details will be collected in the next step.',
            }
          : null
      : deliverPaymentOption === 'practice-invoice'
        ? {
            title: 'Practice will be Invoiced',
            detail: 'A payment link will be emailed to the practice.',
          }
        : deliverPaymentOption === 'third-party'
          ? {
              title: 'Third Party Payment',
              detail: 'Payment details will be collected in the next step.',
            }
          : null;
  const paymentCloseEnabled =
    paymentChoice === 'qr'
      ? paymentCodeScanned
      : paymentChoice === 'send-link'
        ? paymentMethod === 'email'
          ? Boolean(paymentEmail)
          : Boolean(paymentTextNumber)
        : false;
  const primaryActionLabel =
    activeStep === stepLabels.length - 1
      ? 'Close'
      : (activeStep === 3 && !includePaymentStep)
      ? 'Create Order'
      : 'Next';

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography component="span" sx={{ fontWeight: 700 }}>
          New Order Wizard
        </Typography>
        {activeStep > 0 && (
          <Typography component="span" sx={{ fontWeight: 500, color: 'text.secondary' }}>
            {' - '}
            {selectedTest.title}
          </Typography>
        )}
        {patientTitleLabel && (
          <Typography component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>
            {' - '}
            {patientTitleLabel}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          px: { xs: 2, md: 2.5 },
          py: { xs: 1.5, md: 2 },
          overflow: 'hidden',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start" sx={{ minHeight: 520 }}>
          <Card
            sx={{
              border: '1px solid #d8e2e7',
              boxShadow: 'none',
              borderRadius: 2,
              width: { xs: '100%', md: 216 },
              flexShrink: 0,
            }}
          >
            <CardContent sx={{ p: { xs: 1.5, md: 1.75 } }}>
              <Stepper activeStep={activeStep} orientation="vertical">
                {stepLabels.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </Card>

          <Box
            sx={{
              flex: 1,
              width: '100%',
              minHeight: { md: 520 },
              maxHeight: { md: 520 },
              overflowY: 'auto',
              pr: { md: 0.5 },
            }}
          >
            <Box sx={{ p: { xs: 0.5, md: 1 } }}>
            {activeStep === 0 && (
              <Stack spacing={2.5}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Box
                    component="img"
                    src={selectedTest.image}
                    alt={selectedTest.title}
                    sx={{
                      width: { xs: '100%', sm: 220 },
                      height: 150,
                      objectFit: 'cover',
                      borderRadius: 2,
                      backgroundColor: '#eef3f7',
                      flexShrink: 0,
                    }}
                  />
                  <Box>
                    <Typography variant="h5" sx={{ mb: 1 }}>
                      {selectedTest.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5 }}>
                      {selectedTest.description}
                    </Typography>
                    <Chip label="Mock Product" size="small" />
                  </Box>
                </Stack>

                <Card sx={{ border: '1px solid #d8e2e7', boxShadow: 'none', borderRadius: 2 }}>
                  <Box sx={{ p: 2.5 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      What It Measures
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      This mock assay measures a curated bundle of laboratory biomarkers associated with inflammatory signaling,
                      metabolic stress, and neurodegenerative risk patterning. In practical terms, it gives the ordering provider
                      a placeholder snapshot of how the patient may be trending across several clinically interesting pathways.
                    </Typography>
                  </Box>
                </Card>

                <Card sx={{ border: '1px solid #d8e2e7', boxShadow: 'none', borderRadius: 2 }}>
                  <Box sx={{ p: 2.5 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Best For
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Best suited for patients presenting with memory concerns, cognitive change, unexplained fatigue, or complex
                      follow-up scenarios where the clinician wants additional laboratory context. This is mock copy, so the
                      clinical language is intentionally generic and should be treated as placeholder content only.
                    </Typography>
                  </Box>
                </Card>
              </Stack>
            )}

            {activeStep === 1 && (
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="h5" sx={{ mb: 1 }}>
                    Patient Information
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Match an existing patient record or enter a new patient, following the same pattern as the current order workflow.
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  {patientMode === 'existing' && !selectedPatient ? (
                    <Button variant="contained" onClick={() => setPatientMode('new')}>
                      New Patient
                    </Button>
                  ) : patientMode === 'new' ? (
                    <Button variant="text" onClick={() => setPatientMode('existing')}>
                      Back To Search
                    </Button>
                  ) : null}
                </Box>

                {patientMode === 'existing' ? (
                  selectedPatient ? (
                    <Card sx={{ border: '1px solid #d8e2e7', boxShadow: 'none', borderRadius: 2 }}>
                      <Box sx={{ p: 2.5 }}>
                        <Stack spacing={1.5}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
                            <Box>
                              <Typography variant="h6">
                                {selectedPatient.firstName} {selectedPatient.lastName}
                              </Typography>
                            </Box>
                            <Button
                              variant="text"
                              onClick={() => {
                                setSelectedPatientId('');
                                setPatientSearchText('');
                              }}
                            >
                              Change Patient
                            </Button>
                          </Box>
                          <Typography variant="body2">
                            <strong>DOB:</strong> {selectedPatient.dob}
                          </Typography>
                          <Typography variant="body2">
                            <strong>MRN:</strong> {selectedPatient.mrn}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Home Address:</strong> {selectedPatient.address}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Text Number:</strong> {selectedPatient.phone}
                          </Typography>
                        </Stack>
                      </Box>
                    </Card>
                  ) : (
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        label="Search Patients"
                        value={patientSearchText}
                        onChange={(event) => setPatientSearchText(event.target.value)}
                      />

                      <TableContainer component={Paper} sx={{ maxHeight: 320 }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>First Name</TableCell>
                              <TableCell>Last Name</TableCell>
                              <TableCell>DOB</TableCell>
                              <TableCell>MRN</TableCell>
                              <TableCell>Action</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredPatients.map((patient) => (
                              <TableRow key={patient.id}>
                                <TableCell>{patient.firstName}</TableCell>
                                <TableCell>{patient.lastName}</TableCell>
                                <TableCell>{patient.dob}</TableCell>
                                <TableCell>{patient.mrn}</TableCell>
                                <TableCell>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => setSelectedPatientId(patient.id)}
                                  >
                                    Select
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                            {filteredPatients.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={5}>
                                  <Typography variant="body2" color="text.secondary">
                                    No patients match the current search.
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Stack>
                  )
                ) : (
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <Stack spacing={2} sx={{ flex: 1 }}>
                      <TextField
                        fullWidth
                        label="First Name"
                        value={newFirstName}
                        onChange={(event) => setNewFirstName(event.target.value)}
                      />
                      <TextField
                        fullWidth
                        label="Last Name"
                        value={newLastName}
                        onChange={(event) => setNewLastName(event.target.value)}
                      />
                      <TextField
                        fullWidth
                        label="Date of Birth (YYYY-MM-DD)"
                        value={newDob}
                        onChange={(event) => setNewDob(event.target.value)}
                      />
                      <TextField
                        fullWidth
                        label="MRN"
                        value={newMrn}
                        onChange={(event) => setNewMrn(event.target.value)}
                      />
                    </Stack>

                    <Stack spacing={2} sx={{ flex: 1 }}>
                      <TextField
                        fullWidth
                        label="Street Address 1"
                        value={newStreet1}
                        onChange={(event) => setNewStreet1(event.target.value)}
                      />
                      <TextField
                        fullWidth
                        label="Street Address 2"
                        value={newStreet2}
                        onChange={(event) => setNewStreet2(event.target.value)}
                      />
                      <TextField
                        fullWidth
                        label="City"
                        value={newCity}
                        onChange={(event) => setNewCity(event.target.value)}
                      />
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                          fullWidth
                          label="State"
                          value={newState}
                          onChange={(event) => setNewState(event.target.value)}
                        />
                        <TextField
                          fullWidth
                          label="Zip Code"
                          value={newZipCode}
                          onChange={(event) => setNewZipCode(event.target.value)}
                        />
                      </Stack>
                      <TextField
                        fullWidth
                        label="Cell Number"
                        value={newTextNumber}
                        onChange={(event) => setNewTextNumber(event.target.value)}
                      />
                      <TextField
                        fullWidth
                        label="Email"
                        value={newEmail}
                        onChange={(event) => setNewEmail(event.target.value)}
                      />
                    </Stack>
                  </Stack>
                )}
              </Stack>
            )}

            {activeStep === 2 && (
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="h5" sx={{ mb: 1 }}>
                    Kit Delivery
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {selectedTest.description}
                  </Typography>
                </Box>

                {!kitDeliveryOption && (
                  <Stack spacing={2}>
                    <Card
                      onClick={() => {
                        setKitDeliveryOption('inventory');
                        setDeliverPaymentOption('');
                      }}
                      sx={{
                        border: '1px solid #d8e2e7',
                        boxShadow: 'none',
                        borderRadius: 2,
                        cursor: 'pointer',
                      }}
                    >
                      <Box sx={{ p: 2.5 }}>
                        <FormControlLabel
                          checked={false}
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="h6" sx={{ mb: 0.5 }}>
                                In House Inventory
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                The practice has kits on hand and will provide the kit to the patient along with the
                                requisition form.
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: 'flex-start', m: 0, pointerEvents: 'none' }}
                        />
                      </Box>
                    </Card>

                    <Card
                      onClick={() => {
                        setKitDeliveryOption('deliver');
                        setInventoryPaymentOption('');
                      }}
                      sx={{
                        border: '1px solid #d8e2e7',
                        boxShadow: 'none',
                        borderRadius: 2,
                        cursor: 'pointer',
                      }}
                    >
                      <Box sx={{ p: 2.5 }}>
                        <FormControlLabel
                          checked={false}
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="h6" sx={{ mb: 0.5 }}>
                                Deliver Kit
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                A kit will be ordered and delivered to a given address on behalf of the patient. A
                                requisition form will be provided to the patient.
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: 'flex-start', m: 0, pointerEvents: 'none' }}
                        />
                      </Box>
                    </Card>
                  </Stack>
                )}

                {kitDeliveryOption === 'inventory' && (
                  <Card
                    sx={{
                      border: '2px solid #1976d2',
                      boxShadow: 'none',
                      borderRadius: 2,
                    }}
                  >
                    <Box sx={{ p: 2.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="h6" sx={{ mb: 0.5 }}>
                            In House Inventory
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            The practice has kits on hand and will provide the kit to the patient along with the
                            requisition form.
                          </Typography>
                        </Box>
                        <Button
                          variant="text"
                          onClick={() => {
                            setKitDeliveryOption('');
                            setInventoryPaymentOption('');
                          }}
                        >
                          Change Delivery
                        </Button>
                      </Box>

                      <RadioGroup
                        row
                        value={inventoryPaymentOption}
                        onChange={(event) => setInventoryPaymentOption(event.target.value)}
                        sx={{ gap: 2, alignItems: 'stretch' }}
                      >
                        <Card
                          sx={{
                            flex: 1,
                            border: inventoryPaymentOption === 'no-payment' ? '2px solid #1976d2' : '1px solid #d8e2e7',
                            boxShadow: 'none',
                            borderRadius: 2,
                          }}
                        >
                          <Box sx={{ p: 2 }}>
                            <FormControlLabel
                              value="no-payment"
                              control={<Radio />}
                              label={
                                <Box>
                                  <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                                    No Payment at this Time
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    The practice is handling payment out of band.
                                  </Typography>
                                </Box>
                              }
                              sx={{ alignItems: 'flex-start', m: 0 }}
                            />
                          </Box>
                        </Card>

                        <Card
                          sx={{
                            flex: 1,
                            border:
                              inventoryPaymentOption === 'third-party'
                                ? '2px solid #1976d2'
                                : '1px solid #d8e2e7',
                            boxShadow: 'none',
                            borderRadius: 2,
                          }}
                        >
                          <Box sx={{ p: 2 }}>
                            <FormControlLabel
                              value="third-party"
                              control={<Radio />}
                              label={
                                <Box>
                                  <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                                    Third Party Payment
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    A link will be provided for payment.
                                  </Typography>
                                </Box>
                              }
                              sx={{ alignItems: 'flex-start', m: 0 }}
                            />
                          </Box>
                        </Card>
                      </RadioGroup>
                    </Box>
                  </Card>
                )}

                {kitDeliveryOption === 'deliver' && (
                  <Card
                    sx={{
                      border: '2px solid #1976d2',
                      boxShadow: 'none',
                      borderRadius: 2,
                    }}
                  >
                    <Box sx={{ p: 2.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="h6" sx={{ mb: 0.5 }}>
                            Deliver Kit
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            A kit will be ordered and delivered to a given address on behalf of the patient. A
                            requisition form will be provided to the patient.
                          </Typography>
                        </Box>
                        <Button
                          variant="text"
                          onClick={() => {
                            setKitDeliveryOption('');
                            setDeliverPaymentOption('');
                          }}
                        >
                          Change Delivery
                        </Button>
                      </Box>

                      <RadioGroup
                        row
                        value={deliverPaymentOption}
                        onChange={(event) => setDeliverPaymentOption(event.target.value)}
                        sx={{ gap: 2, alignItems: 'stretch' }}
                      >
                            <Card
                              sx={{
                                flex: 1,
                                border:
                                  deliverPaymentOption === 'practice-invoice'
                                    ? '2px solid #1976d2'
                                    : '1px solid #d8e2e7',
                                boxShadow: 'none',
                                borderRadius: 2,
                              }}
                            >
                              <Box sx={{ p: 2 }}>
                                <FormControlLabel
                                  value="practice-invoice"
                                  control={<Radio />}
                                  label={
                                    <Box>
                                      <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                                        Practice will be Invoiced
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        A payment link will be emailed to the practice.
                                      </Typography>
                                    </Box>
                                  }
                                  sx={{ alignItems: 'flex-start', m: 0 }}
                                />
                              </Box>
                            </Card>

                            <Card
                              sx={{
                                flex: 1,
                                border:
                                  deliverPaymentOption === 'third-party'
                                    ? '2px solid #1976d2'
                                    : '1px solid #d8e2e7',
                                boxShadow: 'none',
                                borderRadius: 2,
                              }}
                            >
                              <Box sx={{ p: 2 }}>
                                <FormControlLabel
                                  value="third-party"
                                  control={<Radio />}
                                  label={
                                    <Box>
                                    <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                                      Third Party Payment
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Payment details will be collected in a later step.
                                    </Typography>
                                  </Box>
                                }
                                sx={{ alignItems: 'flex-start', m: 0 }}
                              />
                              </Box>
                            </Card>
                          </RadioGroup>

                      <Stack spacing={1.5} sx={{ mt: 2.5 }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
                          <Stack spacing={1.5} sx={{ flex: 1 }}>
                            <TextField
                              fullWidth
                              label="Street Address 1"
                              size="small"
                              value={deliverStreet1}
                              onChange={(event) => setDeliverStreet1(event.target.value)}
                            />
                            <TextField
                              fullWidth
                              label="Street Address 2"
                              size="small"
                              value={deliverStreet2}
                              onChange={(event) => setDeliverStreet2(event.target.value)}
                            />
                            <TextField
                              fullWidth
                              label="City"
                              size="small"
                              value={deliverCity}
                              onChange={(event) => setDeliverCity(event.target.value)}
                            />
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                              <TextField
                                fullWidth
                                label="State"
                                size="small"
                                value={deliverState}
                                onChange={(event) => setDeliverState(event.target.value)}
                              />
                              <TextField
                                fullWidth
                                label="Zip Code"
                                size="small"
                                value={deliverZipCode}
                                onChange={(event) => setDeliverZipCode(event.target.value)}
                              />
                            </Stack>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <Button variant="text" onClick={clearDeliveryAddress}>
                                Clear Address
                              </Button>
                            </Box>
                          </Stack>

                          <Divider
                            orientation="vertical"
                            flexItem
                            sx={{ display: { xs: 'none', md: 'block' }, borderColor: '#d8e2e7' }}
                          />

                          <Stack spacing={1.25} sx={{ width: { xs: '100%', md: 260 }, flexShrink: 0 }}>
                            {knownAddresses.map((address) => {
                              const lines = formatAddressLines(address);
                              return (
                                <Card
                                  key={address.label}
                                  onClick={() => applyKnownAddress(address)}
                                  sx={{
                                    border: '1px solid #d8e2e7',
                                    boxShadow: 'none',
                                    borderRadius: 2,
                                    cursor: 'pointer',
                                  }}
                                >
                                  <Box sx={{ p: 1.5 }}>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        gap: 1,
                                        mb: 0.5,
                                      }}
                                    >
                                      <Typography variant="subtitle2">
                                        {address.label}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        Select
                                      </Typography>
                                    </Box>
                                    {lines.map((line) => (
                                      <Typography key={line} variant="caption" display="block" color="text.secondary">
                                        {line}
                                      </Typography>
                                    ))}
                                  </Box>
                                </Card>
                              );
                            })}
                          </Stack>
                        </Stack>
                      </Stack>
                    </Box>
                  </Card>
                )}
              </Stack>
            )}

            {activeStep > 2 && (
              activeStep === 3 ? (
                <Stack spacing={2}>
                  <Typography variant="h5">Order Review</Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(auto-fit, minmax(200px, 240px))',
                      },
                      gap: 2,
                    }}
                  >
                    <Card sx={{ border: '1px solid #d8e2e7', boxShadow: 'none', borderRadius: 2 }}>
                      <Box sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>
                          Product
                        </Typography>
                        <Stack spacing={1.25}>
                          <Box
                            component="img"
                            src={selectedTest.image}
                            alt={selectedTest.title}
                            sx={{
                              width: 132,
                              height: 96,
                              objectFit: 'cover',
                              borderRadius: 1.5,
                              backgroundColor: '#eef3f7',
                            }}
                          />
                          <Box>
                            <Typography variant="body2">{selectedTest.title}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {selectedTest.description}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    </Card>

                    <Card sx={{ border: '1px solid #d8e2e7', boxShadow: 'none', borderRadius: 2 }}>
                      <Box sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>
                          Patient
                        </Typography>
                        <Stack spacing={0.75}>
                          {selectedPatient ? (
                            <>
                              <Typography variant="body2">
                                {selectedPatient.firstName} {selectedPatient.lastName}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                DOB: {selectedPatient.dob}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                MRN: {selectedPatient.mrn}
                              </Typography>
                            </>
                          ) : (
                            <>
                              <Typography variant="body2">
                                {[newFirstName, newLastName].filter(Boolean).join(' ')}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                DOB: {newDob || 'Not entered'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                MRN: {newMrn || 'Not entered'}
                              </Typography>
                            </>
                          )}
                        </Stack>
                      </Box>
                    </Card>

                    {kitDeliveryOption === 'deliver' && selectedDeliveryAddressLines.length > 0 && (
                      <Card sx={{ border: '1px solid #d8e2e7', boxShadow: 'none', borderRadius: 2 }}>
                        <Box sx={{ p: 2 }}>
                          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>
                            Shipping Address
                          </Typography>
                          <Stack spacing={0.75}>
                            {selectedDeliveryAddressLines.map((line) => (
                              <Typography key={line} variant="body2" color="text.secondary">
                                {line}
                              </Typography>
                            ))}
                          </Stack>
                        </Box>
                      </Card>
                    )}

                    <Card sx={{ border: '1px solid #d8e2e7', boxShadow: 'none', borderRadius: 2 }}>
                      <Box sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>
                          Payment Details
                        </Typography>
                        <Stack spacing={0.75}>
                          {paymentSummary ? (
                            <>
                              <Typography variant="body2">{paymentSummary.title}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {paymentSummary.detail}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No payment option selected yet.
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    </Card>
                  </Box>
                </Stack>
              ) : (
                <Box>
                  <Typography variant="h5" sx={{ mb: 1 }}>
                    Payment
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2.5 }}>
                    A payment link was created. The payment must be completed before the kit will be shipped.
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 2,
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    }}
                  >
                    <Card sx={{ border: '1px solid #d8e2e7', boxShadow: 'none', borderRadius: 2 }}>
                      <Box sx={{ p: 2 }}>
                        <FormControlLabel
                          value="qr"
                          control={<Radio checked={paymentChoice === 'qr'} onChange={() => setPaymentChoice('qr')} />}
                          label={
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                Payment QR Code
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Third party can scan code to make payment.
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: 'flex-start', m: 0 }}
                        />
                        {paymentChoice === 'qr' && (
                          <>
                            <Box
                              ref={qrFullscreenRef}
                              sx={{
                                mt: 1.5,
                                position: paymentQrFullscreen ? 'fixed' : 'relative',
                                inset: paymentQrFullscreen ? 0 : 'auto',
                                zIndex: paymentQrFullscreen ? 9999 : 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: paymentQrFullscreen ? '100vw' : 'auto',
                                height: paymentQrFullscreen ? '100vh' : 'auto',
                                backgroundColor: paymentQrFullscreen ? '#000' : 'transparent',
                                gap: 2,
                              }}
                            >
                              {paymentQrFullscreen && (
                                <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
                                  <IconButton
                                    aria-label="Close full screen QR view"
                                    onClick={() => {
                                      if (document.fullscreenElement) {
                                        document.exitFullscreen().catch(() => {});
                                      } else {
                                        setPaymentQrFullscreen(false);
                                      }
                                    }}
                                    sx={{ backgroundColor: 'rgba(255,255,255,0.85)' }}
                                  >
                                    X
                                  </IconButton>
                                </Box>
                              )}
                              <img
                                src={`https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=${encodeURIComponent('https://pay.example.com/mock-order')}`}
                                alt="Payment QR Code"
                                style={{ width: 220, height: 220 }}
                              />
                              {paymentQrFullscreen && (
                                <Typography variant="body2" sx={{ color: '#fff', textAlign: 'center', px: 2 }}>
                                  Hold this screen up for the payer to scan. Keep bystanders away for privacy.
                                </Typography>
                              )}
                            </Box>
                            {!paymentQrFullscreen && (
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                                <Button
                                  variant="outlined"
                                  onClick={async () => {
                                    if (!qrFullscreenRef.current) return;
                                    try {
                                      if (qrFullscreenRef.current.requestFullscreen) {
                                        await qrFullscreenRef.current.requestFullscreen();
                                      }
                                      setPaymentQrFullscreen(true);
                                    } catch (err) {
                                      console.error('Failed to enter full screen QR view:', err);
                                    }
                                  }}
                                >
                                  Full Screen
                                </Button>
                              </Box>
                            )}
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={paymentCodeScanned}
                                  onChange={(event) => setPaymentCodeScanned(event.target.checked)}
                                />
                              }
                              label="Code was scanned"
                              sx={{ mt: 1 }}
                            />
                          </>
                        )}
                      </Box>
                    </Card>

                    <Card sx={{ border: '1px solid #d8e2e7', boxShadow: 'none', borderRadius: 2 }}>
                      <Box sx={{ p: 2 }}>
                        <FormControlLabel
                          value="send-link"
                          control={
                            <Radio checked={paymentChoice === 'send-link'} onChange={() => setPaymentChoice('send-link')} />
                          }
                          label={
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                Send Payment Link
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Send the payment link directly to the payer.
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: 'flex-start', m: 0 }}
                        />
                        {paymentChoice === 'send-link' && (
                          <>
                            <RadioGroup
                              value={paymentMethod}
                              onChange={(event) => setPaymentMethod(event.target.value)}
                              sx={{ mt: 1.5, mb: 1.5 }}
                            >
                              <FormControlLabel value="email" control={<Radio />} label="Email" />
                              <FormControlLabel value="text" control={<Radio />} label="Text Number" />
                            </RadioGroup>

                            {paymentMethod === 'email' ? (
                              <TextField
                                select
                                fullWidth
                                size="small"
                                label="Email"
                                value={paymentEmail}
                                onChange={(event) => setPaymentEmail(event.target.value)}
                                sx={{ mb: 2 }}
                              >
                                {patientEmailOptions.length === 0 ? (
                                  <MenuItem value="" disabled>
                                    No patient emails available
                                  </MenuItem>
                                ) : (
                                  patientEmailOptions.map((email) => (
                                    <MenuItem key={email} value={email}>
                                      {email}
                                    </MenuItem>
                                  ))
                                )}
                              </TextField>
                            ) : (
                              <TextField
                                select
                                fullWidth
                                size="small"
                                label="Text Number"
                                value={paymentTextNumber}
                                onChange={(event) => setPaymentTextNumber(event.target.value)}
                                sx={{ mb: 2 }}
                              >
                                {patientTextOptions.length === 0 ? (
                                  <MenuItem value="" disabled>
                                    No patient text numbers available
                                  </MenuItem>
                                ) : (
                                  patientTextOptions.map((phone) => (
                                    <MenuItem key={phone} value={phone}>
                                      {phone}
                                    </MenuItem>
                                  ))
                                )}
                              </TextField>
                            )}

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <Button variant="contained">Send</Button>
                            </Box>
                          </>
                        )}
                      </Box>
                    </Card>
                  </Box>
                </Box>
              )
            )}
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={() => setActiveStep((current) => Math.max(current - 1, 0))}
          disabled={activeStep === 0}
        >
          Back
        </Button>
        <Button
          variant="contained"
          disabled={activeStep === stepLabels.length - 1 && includePaymentStep && !paymentCloseEnabled}
          onClick={() => {
            if (activeStep === stepLabels.length - 1 || (activeStep === 3 && !includePaymentStep)) {
              onOrderCreated?.();
              onClose();
              return;
            }
            setActiveStep((current) => Math.min(current + 1, stepLabels.length - 1));
          }}
        >
          {primaryActionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
